import { ReturnComponent } from '@/utils/reactTypes';
import { Link } from '@mui/material';
import { useState } from 'react';

// One preview length shared across every capped chip list (Territories, Needs,
// Qualifications, Payers, ...) so their "Show all" thresholds stay consistent.
export const DEFAULT_CHIP_PREVIEW_COUNT = 3;

// Shared "Show all N / Show less" capping for wrapped chip rows. The count stays
// the full total so a section subhead can't drift from what's rendered.
export const useCappedList = <T,>(items: readonly T[], previewCount: number) => {
    const [showAll, setShowAll] = useState(false);
    const total = items.length;
    const shown = showAll ? items : items.slice(0, previewCount);
    const canToggle = total > previewCount;
    return { shown, total, canToggle, showAll, toggle: () => setShowAll((prev) => !prev) };
};

export const ShowMoreChipLink = ({
    showAll,
    total,
    onToggle,
}: {
    showAll: boolean;
    total: number;
    onToggle: () => void;
}): ReturnComponent => (
    // Rendered as a block *after* the chip row (not as a flex child inside it), so it
    // always sits on its own line below the chips. `CappedChipRows` wires this up.
    <Link
        component='button'
        type='button'
        variant='caption'
        onClick={onToggle}
        aria-expanded={showAll}
        sx={{ display: 'block', mt: 0.75 }}
    >
        {showAll ? 'Show less' : `+ Show all ${total}`}
    </Link>
);

// Chip row + a "Show all N" / "Show less" toggle beneath it. Encapsulates the
// layout so callers don't each have to place the toggle correctly (below the row,
// on its own line). Pass the already-capped chip elements as children.
export const CappedChipRows = ({
    children,
    canToggle,
    showAll,
    total,
    onToggle,
}: {
    children: ReturnComponent;
    canToggle: boolean;
    showAll: boolean;
    total: number;
    onToggle: () => void;
}): ReturnComponent => (
    <div>
        {children}
        {canToggle && <ShowMoreChipLink showAll={showAll} total={total} onToggle={onToggle} />}
    </div>
);
